/* eslint-disable no-unused-vars */
import React from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import PropTypes from 'prop-types';
import {
  Dropdown as V9Dropdown,
  Label,
  Option,
  makeStyles,
  shorthands,
  FluentProvider,
  webLightTheme,
  Listbox,
} from '@fluentui/react-components';
import { withThemeProvider } from './with-theme-provider';

const useStyles = makeStyles({
  root: {
    // Stack the label above the field with a gap
    display: 'grid',
    gridTemplateRows: 'repeat(1fr)',
    justifyItems: 'start',
    ...shorthands.gap('2px'),
  },
  dropdownRoot: {
    minWidth: '100%',
  },
});

const CustomListBoxRendererInternal = (props) => {
  const { options } = props;

  return (
    <Listbox>
      {options.map(option => (
        <Option key={option.key} value={option.key} disabled={option.disabled || false}>
          {option.text}
        </Option>
                    ))}
    </Listbox>
  );
};

CustomListBoxRendererInternal.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
    disabled: PropTypes.bool,
  })),
};

CustomListBoxRendererInternal.defaultProps = {
  options: [],
};

const CustomListBoxRenderer = withThemeProvider(CustomListBoxRendererInternal);

const DropdownShimInternal = React.forwardRef((props, _ref) => {
  const {
    options,
    className,
    label,
    required,
    onRenderLabel,
    defaultSelectedKeys,
    defaultSelectedKey,
    multiSelect,
    onChange,
    selectedOptions,
    onOptionSelect,
    ...rest
  } = props;

  const onOptionsChangeV8Adapter = (event, item) => {
    // call onChange function with same signature as v8 dropdown
    const commonItems = { key: item.optionValue, text: item.optionText };

    if (multiSelect) {
      onChange(event, { ...commonItems, selected: item.selectedOptions.indexOf(item.optionValue) !== -1 });
    } else {
      onChange(event, commonItems);
    }
  };

  const onOptionsChange = (event, data) => {
    if (onOptionSelect) {
      onOptionSelect(event, data);
    } else {
      onOptionsChangeV8Adapter(event, data);
    }
  };

  const styles = useStyles();

  const getDefaultOptionValue = () => {
    if (defaultSelectedKeys) {
      return _.filter(options, option => defaultSelectedKeys.indexOf(option.key) !== -1).map(option => option.text);
    } else if (defaultSelectedKey) {
      return _.find(options, option => option.key === defaultSelectedKey).text;
    }
    return undefined;
  };

  const computedDefaultValue = getDefaultOptionValue(defaultSelectedKeys || defaultSelectedKey);

  return (
    <div className={classNames(styles.root, className)}>
      {label && !onRenderLabel && <Label weight="semibold" required={required}>{label}</Label>}
      {onRenderLabel && onRenderLabel(props)}
      <V9Dropdown
        listbox={<CustomListBoxRenderer {...props} />}
        defaultValue={computedDefaultValue}
        selectedOptions={selectedOptions}
        defaultSelectedOptions={defaultSelectedKeys}
        multiselect={multiSelect}
        onOptionSelect={onOptionsChange}
        className={styles.dropdownRoot}
      />
    </div>
  );
});

DropdownShimInternal.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })).isRequired,
  defaultSelectedKeys: PropTypes.arrayOf(PropTypes.string),
  multiSelect: PropTypes.bool,
  onChange: PropTypes.func,
  selectedOptions: PropTypes.arrayOf(PropTypes.string),
  onOptionSelect: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  onRenderLabel: PropTypes.func,
  defaultSelectedKey: PropTypes.string,
  className: PropTypes.string,
};

DropdownShimInternal.defaultProps = {
  defaultSelectedKeys: undefined,
  multiSelect: false,
  onChange: undefined,
  selectedOptions: undefined,
  onOptionSelect: undefined,
  label: undefined,
  required: false,
  onRenderLabel: undefined,
  defaultSelectedKey: undefined,
  className: undefined,
};

export const DropdownShim = DropdownShimInternal;
